import React, { useEffect, useState, useContext, useRef, useLayoutEffect } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "./AdminServicesModal.css";
import { Edit, Trash2 } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import BannerMessage from "../../common/bannerMessage/bannerMessage";

const AdminServicesModal = ({ isOpen, onClose, shopId, categories = [], initialServices = [], onOpenCategories, onSave }) => {
	const { language, userData, setUserData } = useContext(CombinedContext);
	const [saving, setSaving] = useState(false);
	const [invalidKeys, setInvalidKeys] = useState(new Set());
	const [bannerMessage, setBannerMessage] = useState("");
	const [activeCategoryId, setActiveCategoryId] = useState(null);
	const bodyRef = useRef(null);

	const categoryRefs = useRef({});
	const isProgrammaticScroll = useRef(false);
	const rowRefs = useRef({});

	const toParts = (totalMinutes = 0) => {
		const h = Math.floor((Number(totalMinutes) || 0) / 60);
		const m = (Number(totalMinutes) || 0) % 60;
		return { hours: h, minutes: m };
	};
	const toMinutes = (h = 0, m = 0) => Number(h) * 60 + Number(m);

	const [rows, setRows] = useState([]);

	useEffect(() => {
		const newRows = rows.filter((r) => r._isNew);
		const newRow = newRows[newRows.length - 1];

		if (!newRow) return;

		const el = rowRefs.current[newRow.key];

		if (el && bodyRef.current) {
			el.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, [rows]);

	useEffect(() => {
		const mapped = (initialServices || []).map((s, idx) => {
			const { hours, minutes } = toParts(s.duration || 0);
			return {
				key: s.serviceId || `row_${idx}_${Date.now()}`,
				serviceId: s.serviceId,
				name: s.name || s.labels?.[language] || "",
				description: s.description || "",
				hours,
				minutes,
				price: s.price ?? 0,
				currency: s.currency || "DOP",
				active: s.active !== false,
				categoryId: s.categoryId || "cat_default",
				isHomeService: s.isHomeService === true,
				transitMinutes: s.transitMinutes ?? 30,
				_markedForDelete: false,
			};
		});

		setRows(
			mapped.length
				? mapped
				: [
						{
							key: `row_${Date.now()}`,
							serviceId: null,
							name: "",
							description: "",
							hours: 0,
							minutes: 30,
							price: "",
							currency: "DOP",
							active: true,
						},
					],
		);
	}, [initialServices, language]);

	const hourOptions = Array.from({ length: 9 }, (_, i) => i);
	const minuteOptions = [0, 15, 30, 45];

	const updateRow = (idx, patch) => {
		setRows((prev) => {
			const next = [...prev];
			next[idx] = { ...next[idx], ...patch };
			return next;
		});
	};

	const addRow = (categoryId) => {
		setRows((prev) => [
			...prev,
			{
				key: `row_${Date.now()}`,
				serviceId: null,
				name: "",
				description: "",
				hours: 0,
				minutes: 30,
				price: "",
				currency: "DOP",
				active: true,
				isHomeService: false,
				transitMinutes: 30,
				categoryId: categoryId || categories[0]?.categoryId || "cat_default",
				_markedForDelete: false,
				_isNew: true, // 👈 permanent until modal closes
			},
		]);
	};

	const removeRow = (idx) => {
		setRows((prev) => {
			const next = [...prev];

			// If it's a NEW row (never saved), remove immediately
			if (!next[idx].serviceId) {
				next.splice(idx, 1);
				return next;
			}

			// Existing service → mark for deletion
			next[idx] = {
				...next[idx],
				_markedForDelete: true,
			};

			return next;
		});
	};

	const getDurationMinutes = (row) => toMinutes(row.hours, row.minutes);

	const isRowValid = (row) => {
		if (!row.name?.trim()) return false;
		if (row.price === "" || row.price == null || Number(row.price) < 0) return false;
		if (getDurationMinutes(row) <= 0) return false;

		// 🔥 ONLY required for home services
		if (row.isHomeService) {
			if (row.transitMinutes === "" || row.transitMinutes == null || Number(row.transitMinutes) <= 0) {
				return false;
			}
		}

		return true;
	};

	useEffect(() => {
		if (!bannerMessage) return;

		const hasInvalid = rows.filter((r) => !r._markedForDelete).some((r) => !isRowValid(r));

		if (!hasInvalid) {
			setBannerMessage("");
			setInvalidKeys(new Set());
		}
	}, [rows]);

	const handleSave = async () => {
		const activeRows = rows.filter((r) => !r._markedForDelete);

		// Validate
		const invalid = activeRows.filter((r) => !isRowValid(r));

		if (invalid.length > 0) {
			setInvalidKeys(new Set(invalid.map((r) => r.key)));
			setBannerMessage(translations[language]?.profile?.errors?.fillAllFields || "Please fill in all required fields.");
			return;
		}

		setInvalidKeys(new Set()); // clear errors
		const toDelete = rows.filter((r) => r._markedForDelete && r.serviceId);

		const toSave = rows.filter((r) => !r._markedForDelete);

		const payload = toSave.map((r) => ({
			serviceId: r.serviceId,
			name: r.name?.trim(),
			description: r.description?.trim(),
			duration: toMinutes(r.hours, r.minutes),
			price: r.price === "" ? 0 : Number(r.price),
			currency: r.currency || "DOP",
			active: r.active !== false,
			isHomeService: r.isHomeService === true,
			transitMinutes: r.isHomeService ? r.transitMinutes : 0,
			categoryId: r.categoryId || "cat_default",
		}));

		try {
			setSaving(true);

			// 1️⃣ Delete removed services
			for (const r of toDelete) {
				await companyBasedRequest.removeShopService(shopId, r.serviceId, userData.user.userId, userData.cognitoTokens.AccessToken);
			}

			// 2️⃣ Save remaining services
			const res = await companyBasedRequest.updateShopServices(shopId, userData.user.userId, payload, userData.cognitoTokens.AccessToken);

			setUserData((prev) => ({
				...prev,
				shop: res.shop,
			}));

		} catch (e) {
			console.error("Failed to save services:", e);
		} finally {
			setSaving(false);
		}
	};

	const visibleRows = rows.filter((r) => !r._markedForDelete);

	const grouped = categories.map((cat) => ({
		cat,
		items: visibleRows.filter((r) => (r.categoryId || "cat_default") === cat.categoryId),
	}));
	useEffect(() => {
		categoryRefs.current = {};
	}, [categories]);

	useLayoutEffect(() => {
		if (!bodyRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (isProgrammaticScroll.current) return;

				const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

				if (!visible.length) return;

				const id = visible[0].target.dataset.catId;
				if (id) setActiveCategoryId(id);
			},
			{
				root: bodyRef.current,

				// 🔥 THIS IS CRITICAL
				rootMargin: "-72px 0px -60% 0px",

				// 🔥 VERY LOW threshold
				threshold: 0.01,
			},
		);

		Object.values(categoryRefs.current).forEach((el) => {
			if (el) observer.observe(el);
		});

		return () => observer.disconnect();
	}, [categories, rows.length]);

	useEffect(() => {
		if (!activeCategoryId && categories.length) {
			setActiveCategoryId(categories[0].categoryId);
		}
	}, [categories, activeCategoryId]);

	return (
		<div className="sf-adminsv-overlay" onClick={onClose}>
			<div className="sf-adminsv-modal" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="sf-adminsv-header">
					<h2 className="sf-adminsv-title">{translations[language]?.profile?.services?.title || "Services"}</h2>
					<button className="sf-adminsv-close" onClick={onClose} aria-label="Close">
						✕
					</button>
				</div>

				{/* Body */}
				<div className="sf-adminsv-body" ref={bodyRef}>
					<div className="sf-adminsv-cat-tabs">
						<div className="sf-adminsv-cat-tabs-inner">
							{categories.map((cat) => (
								<button
									key={cat.categoryId}
									className={`sf-adminsv-cat-tab ${activeCategoryId === cat.categoryId ? "active" : ""}`}
									onClick={() => {
										const el = categoryRefs.current?.[cat.categoryId];
										if (!el || !bodyRef.current) return;

										isProgrammaticScroll.current = true;
										setActiveCategoryId(cat.categoryId);

										const topOffset = 72; // height of tabs
										const y =
											el.getBoundingClientRect().top -
											bodyRef.current.getBoundingClientRect().top +
											bodyRef.current.scrollTop -
											topOffset;

										bodyRef.current.scrollTo({
											top: y,
											behavior: "smooth",
										});

										setTimeout(() => {
											isProgrammaticScroll.current = false;
										}, 450);
									}}
								>
									{cat.name?.[language]}
								</button>
							))}
							<button className="sf-adminsv-cat-tab sf-adminsv-cat-edit" onClick={onOpenCategories} type="button" aria-label="Edit categories">
								<Edit size={14} strokeWidth={1.8} />
							</button>
						</div>
					</div>
					<div className="sf-adminsv-sub-body">
						<DragDropContext
							onDragEnd={(result) => {
								if (!result.destination) return;

								const updated = Array.from(rows);
								const [moved] = updated.splice(result.source.index, 1);
								updated.splice(result.destination.index, 0, moved);

								setRows(updated);
							}}
						>
							<Droppable droppableId="services">
								{(provided) => (
									<div ref={provided.innerRef} {...provided.droppableProps}>
										{(() => {
											const visibleRows = rows.filter((r) => !r._markedForDelete);

											// Map row.key → real index in `rows`
											const keyToRealIndex = visibleRows.reduce((acc, r) => {
												acc[r.key] = rows.findIndex((x) => x.key === r.key);
												return acc;
											}, {});

											// Group rows by category (visual only)
											const grouped = categories.map((cat) => ({
												cat,
												items: visibleRows.filter((r) => (r.categoryId || "cat_default") === cat.categoryId),
											}));

											return grouped.map(({ cat, items }) => {
												return (
													<div
														key={cat.categoryId}
														data-cat-id={cat.categoryId}
														ref={(el) => {
															if (el) categoryRefs.current[cat.categoryId] = el;
														}}
														className="sf-adminsv-category-section"
													>
														<div className="sf-adminsv-category-header">
															<h3 className="sf-adminsv-category-title">{cat.name?.[language]}</h3>

															<button className="sf-adminsv-cat-add-btn" onClick={() => addRow(cat.categoryId)}>
																{translations[language]?.profile?.services?.add || "Add"}
															</button>
														</div>

														{items.length === 0 && (
															<div className="sf-empty-category">{translations[language]?.profile?.services?.emptyCategory}</div>
														)}

														{items.map((row, dndIndex) => {
															const realIdx = keyToRealIndex[row.key];

															return (
																<Draggable key={row.key} draggableId={row.key.toString()} index={dndIndex}>
																	{(dragProvided) => (
																		<div
																			className={`sf-adminsv-block 
                                                                                ${invalidKeys.has(row.key) ? "sf-adminsv-invalid" : ""}
                                                                                ${row._isNew && !invalidKeys.has(row.key) ? "sf-adminsv-new" : ""}
                                                                            `}
																			ref={(el) => {
																				dragProvided.innerRef(el);
																				if (el) rowRefs.current[row.key] = el;
																			}}
																			{...dragProvided.draggableProps}
																		>
																			<div>
																				<div className="sf-adminsv-line">
																					{/* Name */}
																					<div className="sf-adminsv-field">
																						<label className="sf-adminsv-label">
																							{translations[language]?.profile?.services?.name}
																						</label>
																						<input
																							className="sf-adminsv-input"
																							placeholder={
																								translations[language]?.profile?.services?.placeholderName ||
																								"Service name"
																							}
																							value={row.name}
																							onChange={(e) =>
																								updateRow(realIdx, {
																									name: e.target.value,
																								})
																							}
																						/>
																					</div>

																					{/* Price */}
																					<div className="sf-adminsv-field">
																						<label className="sf-adminsv-label">
																							{translations[language]?.profile?.services?.price}
																						</label>
																						<div className="sf-adminsv-price">
																							<span className="sf-adminsv-currency">{row.currency || "DOP"}</span>
																							<input
																								className="sf-adminsv-input sf-adminsv-input-price"
																								inputMode="decimal"
																								pattern="[0-9]*"
																								placeholder="0"
																								value={row.price}
																								onChange={(e) => {
																									const v = e.target.value.replace(/[^\d]/g, "");
																									updateRow(realIdx, {
																										price: v,
																									});
																								}}
																							/>
																						</div>
																					</div>

																					{/* Hours */}
																					<div className="sf-adminsv-field">
																						<label className="sf-adminsv-label">
																							{translations[language]?.profile?.services?.hours}
																						</label>
																						<select
																							className="sf-adminsv-select"
																							value={row.hours}
																							onChange={(e) =>
																								updateRow(realIdx, {
																									hours: Number(e.target.value),
																								})
																							}
																						>
																							{hourOptions.map((h) => (
																								<option key={h} value={h}>
																									{h}
																								</option>
																							))}
																						</select>
																					</div>

																					{/* Minutes */}
																					<div className="sf-adminsv-field">
																						<label className="sf-adminsv-label">
																							{translations[language]?.profile?.services?.minutes}
																						</label>
																						<div className="sf-adminsv-field-bottom">
																							<select
																								className="sf-adminsv-select"
																								value={row.minutes}
																								onChange={(e) =>
																									updateRow(realIdx, {
																										minutes: Number(e.target.value),
																									})
																								}
																							>
																								{minuteOptions.map((m) => (
																									<option key={m} value={m}>
																										{m}
																									</option>
																								))}
																							</select>
																						</div>
																					</div>
																				</div>

																				{/* Description + Category */}
																				<div className="sf-adminsv-bottom-section">
																					<label className="sf-adminsv-label">
																						{translations[language]?.profile?.services?.description}
																					</label>
																					<input
																						className="sf-adminsv-input sf-adminsv-input-desc"
																						placeholder={
																							translations[language]?.profile?.services?.placeholderDescription ||
																							"Service description"
																						}
																						value={row.description}
																						onChange={(e) =>
																							updateRow(realIdx, {
																								description: e.target.value,
																							})
																						}
																					/>

																					<div className="sf-adminsv-field">
																						<label className="sf-adminsv-label">
																							{translations[language]?.profile?.services?.category}
																						</label>
																						<select
																							className="sf-adminsv-select"
																							value={row.categoryId || "cat_default"}
																							onChange={(e) =>
																								updateRow(realIdx, {
																									categoryId: e.target.value,
																								})
																							}
																						>
																							{categories.map((cat) => (
																								<option key={cat.categoryId} value={cat.categoryId}>
																									{cat.name?.[language] || "Unnamed"}
																								</option>
																							))}
																						</select>
																					</div>
																				</div>
																				{row.isHomeService && (
																					<div className="sf-adminsv-home-extra">
																						<label className="sf-adminsv-label">
																							{translations[language]?.profile?.services?.transitTimeLabel}
																						</label>

																						<input
																							className="sf-adminsv-input sf-adminsv-input-transit"
																							type="number"
																							min="0"
																							step="5"
																							value={row.transitMinutes}
																							onChange={(e) =>
																								updateRow(realIdx, {
																									transitMinutes: e.target.value,
																								})
																							}
																						/>
																					</div>
																				)}
																			</div>

																			{/* Right controls */}
																			<div className="sf-adminsv-block-header">
																				<div
																					className="sf-adminsv-drag-handle"
																					{...dragProvided.dragHandleProps}
																					aria-label="Reorder service"
																				>
																					⋮⋮
																				</div>
																				<button
																					type="button"
																					className={`sf-adminsv-home-btn ${row.isHomeService ? "active" : ""}`}
																					onClick={() =>
																						updateRow(realIdx, {
																							isHomeService: !row.isHomeService,
																						})
																					}
																				>
																					{row.isHomeService
																						? translations[language]?.profile?.services?.homeService ||
																							"Home service"
																						: translations[language]?.profile?.services?.inShop || "In shop"}
																				</button>

																				<button
																					className="sf-adminsv-iconbtn sf-adminsv-remove"
																					onClick={() => removeRow(realIdx)}
																				>
																					<Trash2 size={20} strokeWidth={1.8} />
																				</button>
																			</div>
																		</div>
																	)}
																</Draggable>
															);
														})}
													</div>
												);
											});
										})()}

										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</div>
				{bannerMessage && <BannerMessage type="error" text={bannerMessage} />}

				{/* Footer */}
				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AdminServicesModal;
